import {calculateMealMetrics, getMetrics} from "@client/calculations";
import * as dialogs from "@client/features/dialogs";
import {notify} from "@client/features/notifications";
import * as search from "@client/features/search";
import * as forms from "@client/forms";
import * as router from "@client/router";
import {AppDispatch} from "@client/store";
import {server} from "@reactivated";
import {produce} from "immer";
import React from "react";

export const useRecipeForm = ({
    initial,
    disabled,
}: {
    initial?: Partial<server.journal.nutrition.CreateRecipe.values> | null;
    disabled?: true;
}) => {
    const form = forms.useForm(server.journal.nutrition.CreateRecipe, {
        initial,
    });

    const recipeLayout = forms.layout(form, {
        sections: [
            {
                name: "recipe",
                label: "Recipe",
                fields: [
                    "name",
                    {
                        row: [
                            {name: "container_servings", label: null},
                            {name: "serving_size_unit", label: null},
                        ],
                        label: "Recipe units",
                    },
                    {
                        row: [
                            {name: "container_quantity", label: null},
                            {name: "container_unit", label: null},
                        ],
                        label: "Recipe weight",
                    },
                    "notes",
                ],
            },
        ],
        hidden: [],
    });
    const layout = {
        hasErrors: recipeLayout.byName.recipe.hasErrors,
        component: (
            <div className="flex flex-col gap-[15px]">
                <forms.Fields
                    form={form}
                    layout={recipeLayout.byName.recipe}
                    readOnly={disabled}
                />
            </div>
        ),
    };

    return {form, layout};
};

export const confirmAndDeleteRecipe =
    (recipe: server.journal.nutrition.Recipe.output) =>
    async (dispatch: AppDispatch) => {
        const confirmed = await dispatch(
            dialogs.confirm({
                message: "Are you sure?",
                body: "This will be deleted forever.",
            }),
        );

        if (confirmed) {
            await server.journal.nutrition.delete_recipe({recipe_id: recipe.id});
            void dispatch(search.loadSearch({reset: true}));
            void router.push("recipe_list.index", {});
            dispatch(notify(`Your recipe was deleted`));
        }
    };

export function addEntriesToRecipe({
    recipe,
    entries,
}: {
    recipe: server.journal.nutrition.Recipe.output;
    entries: server.journal.nutrition.ExpandedEntry.output[];
}) {
    const createdEntries = entries.map((entry) => ({
        ...entry,
        expanded_recipe: entry.expanded_recipe.map((ingredient) => ({
            ...ingredient,
            uuid: crypto.randomUUID(),
        })),
        uuid: crypto.randomUUID(),
    }));

    const updatedRecipe = produce(recipe, (draftState) => {
        draftState.expanded_recipe = draftState.expanded_recipe.concat(createdEntries);

        const recipeCalculations = calculateMealMetrics({
            entries: draftState.expanded_recipe,
        });

        if (draftState.expanded_recipe.length > 0) {
            for (const [metric, metricPg] of getMetrics({useKilojoules: false})
                .ALL_METRICS) {
                draftState[metricPg] =
                    recipeCalculations[metric]
                        ?.dividedBy(draftState.container_servings)
                        .toString() ?? null;
            }
        }
    });

    void server.journal.nutrition.update_recipe({
        recipe_uuid: recipe.uuid,
        entries: updatedRecipe.expanded_recipe,
    });

    return updatedRecipe;
}
